import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getContrastTextColor } from '../lib/utils';
import { getStale, setCached } from '../lib/pageCache';
import { filterJobsByQuery } from '../lib/jobSearch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Receipt,
  Plus,
  Trash2,
  Send,
  Check,
  X,
  Download,
  Upload,
  Camera,
  ChevronLeft,
  ChevronRight,
  FileText,
  Clock,
  DollarSign,
  Calendar,
  Briefcase,
  Eye,
  Loader2,
  Mail,
  Users,
  Pencil,
  User,
  Printer,
  CheckCircle,
  XCircle,
  Search,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks } from 'date-fns';
import StatTile from '../components/StatTile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPENSE_CATEGORIES = [
  'Fuel/Mileage',
  'Materials/Supplies',
  'Equipment',
  'Meals',
  'Other'
];

export default function ExpenseReports() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isManager = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'super_user';
  const isSuperUser = user?.role === 'super_user';
  // Maintenance / support staff with this permission can add expenses to ANY
  // job, not just the ones they're assigned to. (Asked for by Cory Parks /
  // Joey Brown — maintenance workers needing to charge items to any job.)
  const canExpenseAnyJob = isManager || !!user?.permissions?.expense_any_job;

  const [activeTab, setActiveTab] = useState('my-reports');
  const [loading, setLoading] = useState(true);
  const [myReports, setMyReports] = useState([]);
  const [pendingReports, setPendingReports] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [teamMemberReports, setTeamMemberReports] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]); // All jobs for manager view

  // "My Assigned Jobs" quick-pick + search on the main page (always visible,
  // not tucked in a dropdown). Search covers the same job set the "Job"
  // dropdown already uses — just full-text instead of scroll-to-find.
  const [expenseJobSearch, setExpenseJobSearch] = useState('');
  // Runs the job_id-from-URL prefill (Add Expense from a Job page) exactly
  // once per page load, same pattern as the field report forms.
  const jobContextHandledRef = useRef(false);
  
  // Current week state
  const [currentWeek, setCurrentWeek] = useState(null);
  const [currentYear, setCurrentYear] = useState(null);
  const [weekStart, setWeekStart] = useState(null);
  const [weekEnd, setWeekEnd] = useState(null);
  
  // Current report being edited
  const [currentReport, setCurrentReport] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingForUser, setEditingForUser] = useState(null); // For admin editing another user's report
  
  // New expense form
  const [newExpense, setNewExpense] = useState({
    date: '',
    job_id: '',
    job_number: '',
    category: '',
    description: '',
    amount: 0,
    custom_job_description: '' // For "Other" job option
  });
  
  // Receipt upload
  const [uploadingReceipt, setUploadingReceipt] = useState(null);
  
  // Preview dialog
  const [showPreview, setShowPreview] = useState(false);
  const [previewReport, setPreviewReport] = useState(null);
  
  // Reject dialog
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [reportToReject, setReportToReject] = useState(null);
  
  // Receipt viewer
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [receiptImageUrl, setReceiptImageUrl] = useState(null);
  const [loadingReceipt, setLoadingReceipt] = useState(false);

  // Fetch receipt image - handles both Cloudinary URLs and direct file responses
  const fetchReceiptImage = async (reportId, expenseId, filename) => {
    setLoadingReceipt(true);
    setViewingReceipt({ reportId, expenseId, filename });
    try {
      // First, try to get the expense info to check if it has a direct receipt_url
      const reportRes = await axios.get(`${API}/expense-reports/${reportId}`);
      const expense = reportRes.data.expenses?.find(e => e.id === expenseId);
      
      if (expense?.receipt_url) {
        // Direct Cloudinary URL - just use it directly
        setReceiptImageUrl(expense.receipt_url);
      } else if (expense?.receipt_filename) {
        // Has filename but no URL - try fetching from backend endpoint
        try {
          const response = await axios.get(
            `${API}/expense-reports/${reportId}/expenses/${expenseId}/receipt`,
            { responseType: 'blob' }
          );
          const imageUrl = URL.createObjectURL(response.data);
          setReceiptImageUrl(imageUrl);
        } catch (fetchError) {
          // If backend fails, receipt file is missing - notify user
          toast.error('Receipt file not found. Please re-upload the receipt.');
          setViewingReceipt(null);
          return;
        }
      } else {
        toast.error('No receipt uploaded for this expense');
        setViewingReceipt(null);
        return;
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to load receipt';
      toast.error(errorMsg);
      setViewingReceipt(null);
    } finally {
      setLoadingReceipt(false);
    }
  };

  // Clean up blob URL when closing viewer
  const closeReceiptViewer = () => {
    if (receiptImageUrl) {
      URL.revokeObjectURL(receiptImageUrl);
    }
    setReceiptImageUrl(null);
    setViewingReceipt(null);
  };

  // Get current week info
  useEffect(() => {
    const now = new Date();
    const week = getWeekNumber(now);
    const year = now.getFullYear();
    setCurrentWeek(week);
    setCurrentYear(year);
    updateWeekDates(year, week);
  }, []);

  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const updateWeekDates = (year, week) => {
    // ISO week calculation - Week 1 contains Jan 4th and starts on Monday
    const jan4 = new Date(year, 0, 4);
    // Get day of week (0=Sun, 1=Mon, ..., 6=Sat), convert to ISO (Mon=1, Sun=7)
    const dayOfWeek = jan4.getDay() || 7; // Sunday becomes 7
    // Find Monday of week 1
    const mondayOfWeek1 = new Date(jan4);
    mondayOfWeek1.setDate(jan4.getDate() - dayOfWeek + 1);
    // Calculate start of requested week
    const ws = new Date(mondayOfWeek1);
    ws.setDate(mondayOfWeek1.getDate() + (week - 1) * 7);
    const we = new Date(ws);
    we.setDate(ws.getDate() + 6);
    setWeekStart(ws);
    setWeekEnd(we);
  };

  const fetchData = useCallback(async (opts = {}) => {
    const cacheKey = `expense-reports:${isManager ? 'mgr' : 'user'}:${canExpenseAnyJob ? 'all' : 'my'}`;
    if (opts.useStalePaint) {
      const cached = getStale(cacheKey);
      if (cached) {
        // Paint instantly from cache, then refetch in background.
        setMyReports(cached.myReports || []);
        setJobs(cached.jobs || []);
        if (cached.pendingReports) setPendingReports(cached.pendingReports);
        if (cached.allReports) setAllReports(cached.allReports);
        if (cached.teamMembers) setTeamMembers(cached.teamMembers);
        if (cached.allJobs) setAllJobs(cached.allJobs);
        setLoading(false);
      } else {
        setLoading(true);
      }
    } else {
      setLoading(true);
    }
    try {
      // Users with `expense_any_job` permission (e.g., maintenance staff) see
      // every active job, not just their assignments.
      const myJobsEndpoint = canExpenseAnyJob ? `${API}/jobs` : `${API}/jobs/my`;
      const [reportsRes, jobsRes] = await Promise.all([
        axios.get(`${API}/expense-reports`),
        axios.get(myJobsEndpoint)
      ]);
      setMyReports(reportsRes.data);
      setJobs(jobsRes.data);

      let mgrData = {};
      if (isManager) {
        const [pendingRes, allRes, teamRes, allJobsRes] = await Promise.all([
          axios.get(`${API}/expense-reports/pending`),
          axios.get(`${API}/expense-reports?all_users=true`),
          axios.get(`${API}/team`),
          axios.get(`${API}/jobs`)
        ]);
        setPendingReports(pendingRes.data);
        setAllReports(allRes.data);
        setTeamMembers(teamRes.data);
        setAllJobs(allJobsRes.data);
        mgrData = {
          pendingReports: pendingRes.data,
          allReports: allRes.data,
          teamMembers: teamRes.data,
          allJobs: allJobsRes.data,
        };
      }

      // Overwrite the cache with fresh data. Mutation callers hit this
      // path with `useStalePaint=false`, so their post-mutation state
      // lands in cache without any stale-flash for the next visitor.
      setCached(cacheKey, {
        myReports: reportsRes.data,
        jobs: jobsRes.data,
        ...mgrData,
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load expense reports');
    } finally {
      setLoading(false);
    }
  }, [isManager, canExpenseAnyJob]);

  useEffect(() => {
    // Initial mount → stale-paint OK. Later refetches (from mutations)
    // go through fetchData() directly and skip the stale paint.
    fetchData({ useStalePaint: true });
  }, [fetchData]);

  // Fetch reports for a specific team member
  const fetchTeamMemberReports = async (userId) => {
    try {
      const response = await axios.get(`${API}/expense-reports?all_users=true`);
      const memberReports = response.data.filter(r => r.user_id === userId);
      setTeamMemberReports(memberReports);
    } catch (error) {
      toast.error('Failed to load team member reports');
    }
  };

  const handleSelectTeamMember = (member) => {
    setSelectedTeamMember(member);
    fetchTeamMemberReports(member.id);
  };

  // Edit report for another user (admin only)
  const handleEditTeamMemberReport = (report) => {
    setEditingForUser({
      id: report.user_id,
      name: report.user_name,
      email: report.user_email
    });
    setCurrentReport(report);
    setShowEditor(true);
  };

  // Create new report for a team member (admin only)
  const handleCreateReportForMember = async (member) => {
    try {
      // Create a new report for the selected user
      const response = await axios.post(`${API}/expense-reports`, {
        user_id: member.id,
        week_number: currentWeek,
        year: currentYear
      });
      setEditingForUser({
        id: member.id,
        name: member.full_name,
        email: member.email
      });
      setCurrentReport(response.data);
      setShowEditor(true);
      toast.success(`Created expense report for ${member.full_name}`);
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already exists')) {
        toast.error('A report already exists for this week');
      } else {
        toast.error('Failed to create report');
      }
    }
  };

  const navigateWeek = (direction) => {
    let newWeek = currentWeek + direction;
    let newYear = currentYear;
    
    if (newWeek < 1) {
      newYear--;
      newWeek = 52;
    } else if (newWeek > 52) {
      newYear++;
      newWeek = 1;
    }
    
    setCurrentWeek(newWeek);
    setCurrentYear(newYear);
    updateWeekDates(newYear, newWeek);
  };

  const loadOrCreateReport = async () => {
    try {
      const res = await axios.get(`${API}/expense-reports/week/${currentYear}/${currentWeek}`);
      setCurrentReport(res.data);
      setShowEditor(true);
      // Reset new expense date to current week
      setNewExpense(prev => ({
        ...prev,
        date: weekStart ? format(weekStart, 'yyyy-MM-dd') : ''
      }));
    } catch (error) {
      toast.error('Failed to load expense report');
    }
  };

  // Shared by the "My Assigned Jobs" quick-pick cards AND the job_id-from-
  // URL prefill below — loads/creates this week's report and pre-selects
  // the given job, instead of leaving the user to hunt for it manually.
  const openWeekReportForJob = async (job) => {
    try {
      const res = await axios.get(`${API}/expense-reports/week/${currentYear}/${currentWeek}`);
      const report = res.data;
      setCurrentReport(report);
      setShowEditor(true);
      setNewExpense(prev => ({
        ...prev,
        date: weekStart ? format(weekStart, 'yyyy-MM-dd') : '',
        job_id: job?.id || '',
        job_number: job?.job_number || ''
      }));
      if (report.status !== 'Draft') {
        // Don't fail silently — explain why the Job field / Add Expense
        // form won't be usable once the dialog opens.
        toast.info(`Week ${report.week_number}'s report is already ${report.status.toLowerCase()} and locked for edits.`);
      } else if (job) {
        toast.success(`Ready to add an expense for ${job.job_number || job.client_name || job.job_title || 'this job'}`);
      }
    } catch (error) {
      toast.error("Failed to load this week's expense report");
    }
  };

  // Add Expense clicked from a Job page ("?job_id=...") — auto-open this
  // week's report with that job pre-selected. If the job isn't in the
  // user's normal job list (not assigned to them), fetch it directly so
  // the prefill still works instead of silently showing "No job".
  useEffect(() => {
    const jobId = searchParams.get('job_id');
    if (!jobId || jobContextHandledRef.current || loading || !currentYear || !currentWeek) return;
    jobContextHandledRef.current = true;

    (async () => {
      let job = jobs.find(j => j.id === jobId);
      if (!job) {
        try {
          const res = await axios.get(`${API}/jobs/${jobId}/field-report-prefill`);
          if (res.data?.id) {
            job = res.data;
            setJobs(prev => prev.some(j => j.id === job.id) ? prev : [...prev, job]);
          }
        } catch (err) {
          toast.error("Couldn't find that job — pick it manually below");
        }
      }
      openWeekReportForJob(job);
    })();
  }, [searchParams, loading, currentYear, currentWeek, jobs]);

  const handleAddExpense = async () => {
    if (!newExpense.date || !newExpense.category || !newExpense.description || newExpense.amount <= 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate "Other" option requires a custom description
    if (newExpense.job_id === 'other' && !newExpense.custom_job_description?.trim()) {
      toast.error('Please provide a description for the non-job expense');
      return;
    }
    
    // Build the expense object
    const expense = {
      id: `temp-${Date.now()}`,
      date: newExpense.date,
      // For "Other", set job_id to null and use custom_job_description
      job_id: newExpense.job_id === 'other' ? null : (newExpense.job_id || null),
      job_number: newExpense.job_id === 'other' ? 'OTHER' : (newExpense.job_number || null),
      custom_job_description: newExpense.job_id === 'other' ? newExpense.custom_job_description : null,
      category: newExpense.category,
      description: newExpense.description,
      amount: parseFloat(newExpense.amount)
    };
    
    // Update local state first
    const updatedExpenses = [...(currentReport.expenses || []), expense];
    const updatedTotal = (currentReport.total_amount || 0) + expense.amount;
    
    setCurrentReport(prev => ({
      ...prev,
      expenses: updatedExpenses,
      total_amount: updatedTotal
    }));
    
    // Reset form immediately for better UX
    setNewExpense({
      date: weekStart ? format(weekStart, 'yyyy-MM-dd') : '',
      job_id: '',
      job_number: '',
      category: '',
      description: '',
      amount: 0,
      custom_job_description: ''
    });
    
    // Auto-save to backend so receipt uploads work immediately
    try {
      await axios.put(`${API}/expense-reports/${currentReport.id}`, {
        expenses: updatedExpenses,
        notes: currentReport.notes
      });
      toast.success('Expense added & saved');
      fetchData(); // Refresh to get proper IDs from backend
    } catch (error) {
      console.error('Auto-save failed:', error);
      toast.error('Expense added locally - click Save Draft to persist');
    }
  };

  const handleRemoveExpense = (expenseId) => {
    const expense = currentReport.expenses.find(e => e.id === expenseId);
    setCurrentReport(prev => ({
      ...prev,
      expenses: prev.expenses.filter(e => e.id !== expenseId),
      total_amount: prev.total_amount - (expense?.amount || 0)
    }));
  };

  const handleSaveReport = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/expense-reports/${currentReport.id}`, {
        expenses: currentReport.expenses,
        notes: currentReport.notes
      });
      toast.success('Expense report saved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitReport = async () => {
    if (!currentReport.expenses || currentReport.expenses.length === 0) {
      toast.error('Add at least one expense before submitting');
      return;
    }
    
    setSaving(true);
    try {
      // Save first
      await axios.put(`${API}/expense-reports/${currentReport.id}`, {
        expenses: currentReport.expenses,
        notes: currentReport.notes
      });
      // Then submit
      await axios.post(`${API}/expense-reports/${currentReport.id}/submit`);
      toast.success('Expense report submitted for approval');
      setShowEditor(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDraft = async () => {
    if (!currentReport || currentReport.status !== 'Draft') return;
    
    setSaving(true);
    try {
      await axios.delete(`${API}/expense-reports/${currentReport.id}`);
      toast.success('Draft report deleted');
      setShowEditor(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete draft');
    } finally {
      setSaving(false);
    }
  };

  const handleReceiptUpload = async (expenseId, file) => {
    setUploadingReceipt(expenseId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/expense-reports/${currentReport.id}/expenses/${expenseId}/receipt`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      // Get the receipt_url from the response (backend returns "url")
      const receiptUrl = response.data?.url;
      
      // Update local state with both filename AND url
      setCurrentReport(prev => ({
        ...prev,
        expenses: prev.expenses.map(e => 
          e.id === expenseId ? { ...e, receipt_filename: file.name, receipt_url: receiptUrl } : e
        )
      }));
      
      toast.success('Receipt uploaded');
      
      // Also refresh from backend to ensure sync
      fetchData();
    } catch (error) {
      toast.error('Failed to upload receipt');
    } finally {
      setUploadingReceipt(null);
    }
  };

  const handleApprove = async (reportId) => {
    try {
      await axios.post(`${API}/expense-reports/${reportId}/approve`);
      toast.success('Expense report approved');
      fetchData();
      setShowPreview(false);
    } catch (error) {
      toast.error('Failed to approve report');
    }
  };

  const handleReject = async () => {
    try {
      await axios.post(`${API}/expense-reports/${reportToReject}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success('Expense report rejected');
      setShowRejectDialog(false);
      setRejectReason('');
      setReportToReject(null);
      fetchData();
      setShowPreview(false);
    } catch (error) {
      toast.error('Failed to reject report');
    }
  };

  const handleExportCSV = async (reportId) => {
    try {
      const response = await axios.get(`${API}/expense-reports/${reportId}/export/csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expense_report.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  // PDF generation is now handled via /expense-report/:id/preview with browser print

  const handleEmailReport = (report) => {
    const subject = encodeURIComponent(`Expense Report - ${report.user_name} - Week ${report.week_number}, ${report.year}`);
    const expensesList = report.expenses?.map(exp => 
      `  • ${exp.date}: ${exp.category} - ${exp.description} - $${(exp.amount || 0).toFixed(2)}`
    ).join('\n') || 'No expenses';
    
    const body = encodeURIComponent(
`CENTER LINE LOCATING EXPENSE REPORT

Employee: ${report.user_name}
Email: ${report.user_email || 'N/A'}
Week: ${report.week_number}, ${report.year} (${report.week_start} to ${report.week_end})
Status: ${report.status}
Total Amount: $${(report.total_amount || 0).toFixed(2)}

EXPENSES:
${expensesList}

${report.notes ? `Notes: ${report.notes}` : ''}

---
This expense report was generated from Center Line Locating.`
    );
    
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    toast.success('Email client opened');
  };

  // Team Export Functions
  const handleExportTeamExpenseCSV = () => {
    // Filter reports for current week from all team members
    const currentWeekReports = allReports.filter(
      r => r.week_number === currentWeek && r.year === currentYear
    );
    
    if (currentWeekReports.length === 0) {
      toast.error('No team expense reports for this week');
      return;
    }
    
    // Create summary data
    const exportData = currentWeekReports.map(report => ({
      'Employee': report.user_name,
      'Status': report.status,
      'Expenses Count': report.expenses?.length || 0,
      'Total Amount': formatCurrency(report.total_amount)
    }));
    
    // Add total row
    const totalAmount = currentWeekReports.reduce((sum, r) => sum + (r.total_amount || 0), 0);
    exportData.push({
      'Employee': 'TEAM TOTAL',
      'Status': '',
      'Expenses Count': currentWeekReports.reduce((sum, r) => sum + (r.expenses?.length || 0), 0),
      'Total Amount': formatCurrency(totalAmount)
    });
    
    // Generate filename with week info
    const filename = `team-expenses-week${currentWeek}-${currentYear}.csv`;
    
    // Convert to CSV
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => headers.map(h => `"${row[h]}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    toast.success('Team expense CSV downloaded');
  };

  const handlePrintTeamExpenseView = () => {
    // Filter reports for current week from all team members
    const currentWeekReports = allReports.filter(
      r => r.week_number === currentWeek && r.year === currentYear
    );
    
    if (currentWeekReports.length === 0) {
      toast.error('No team expense reports for this week');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to open print view');
      return;
    }

    const tableRows = currentWeekReports.map(report => `
      <tr>
        <td>${report.user_name}</td>
        <td style="text-align: center;">
          <span style="padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; background: ${
            report.status === 'Approved' ? '#dcfce7' : 
            report.status === 'Submitted' ? '#fef3c7' : 
            report.status === 'Draft' ? '#f1f5f9' : '#fee2e2'
          }; color: ${
            report.status === 'Approved' ? '#166534' : 
            report.status === 'Submitted' ? '#b45309' : 
            report.status === 'Draft' ? '#475569' : '#dc2626'
          };">${report.status}</span>
        </td>
        <td style="text-align: center;">${report.expenses?.length || 0}</td>
        <td style="text-align: right; font-weight: 600; color: #059669;">${formatCurrency(report.total_amount)}</td>
      </tr>
    `).join('');

    const totalAmount = currentWeekReports.reduce((sum, r) => sum + (r.total_amount || 0), 0);
    const totalExpenses = currentWeekReports.reduce((sum, r) => sum + (r.expenses?.length || 0), 0);

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Team Expense Reports - Week ${currentWeek}, ${currentYear}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; max-width: 850px; margin: 0 auto; background: #fff; color: #1e293b; }
          
          /* Header - 3 column layout matching new style */
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 1px solid #e2e8f0; }
          .logo-section { flex-shrink: 0; }
          .logo-section img { height: 80px; width: auto; object-fit: contain; }
          .company-info { flex: 1; text-align: center; padding: 0 20px; font-size: 12px; color: #64748b; line-height: 1.5; }
          .company-info .address { font-weight: 500; color: #334155; }
          .company-info .address-secondary { color: #64748b; margin-top: 2px; }
          .company-info .contact { margin-top: 6px; display: flex; align-items: center; justify-content: center; gap: 16px; font-size: 11px; }
          .company-info .contact span { display: flex; align-items: center; gap: 4px; }
          .company-info .contact svg { width: 12px; height: 12px; color: #64748b; }
          .doc-id { text-align: right; flex-shrink: 0; }
          .doc-id .doc-label { font-size: 10px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; }
          .doc-id .doc-number { font-size: 16px; font-weight: 700; color: #1e293b; margin-top: 2px; }
          
          /* Info Section */
          .info-section { background: #f8fafc; border-radius: 8px; padding: 20px; margin-bottom: 25px; }
          .info-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
          .info-item label { font-size: 10px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 4px; }
          .info-item span { font-size: 14px; color: #1e293b; font-weight: 500; }
          .total-badge { display: inline-block; background: #dcfce7; color: #166534; padding: 6px 16px; border-radius: 20px; font-weight: 700; font-size: 16px; }
          
          /* Table */
          table { width: 100%; border-collapse: collapse; margin-bottom: 25px; font-size: 12px; }
          th { background: #f1f5f9; color: #475569; padding: 12px 10px; text-align: left; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; }
          td { padding: 10px; border-bottom: 1px solid #e2e8f0; }
          tr:hover { background: #f8fafc; }
          .total-row { background: #f8fafc !important; font-weight: 600; }
          .total-row td { border-bottom: none; border-top: 2px solid #e2e8f0; }
          
          /* Footer */
          .footer { text-align: center; color: #94a3b8; font-size: 11px; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; }
          
          /* Print Button */
          .print-btn { 
            position: fixed; top: 20px; right: 20px; 
            background: #ea580c; color: white; border: none; 
            padding: 12px 24px; font-size: 14px; font-weight: 600;
            cursor: pointer; border-radius: 6px;
            display: flex; align-items: center; gap: 8px;
          }
          .print-btn:hover { background: #c2410c; }
          
          @media print { 
            .print-btn { display: none; } 
            body { padding: 20px; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
          }
        </style>
      </head>
      <body>
        <button class="print-btn" onclick="window.print()">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
          Print / Save PDF
        </button>
        
        <!-- Header - New 3-column layout -->
        <div class="header">
          <div class="logo-section">
            <img src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/69b5afabd540121a86879b5b/e29b4c6b2_CLLLogoTransparent.png" alt="Center Line Locating" />
          </div>
          <div class="company-info">
            <div class="address">12030 Goodrich Dr, Charlotte, NC 28273</div>
            <div class="address-secondary">PO Box 38251, Charlotte, NC 28278</div>
            <div class="contact">
              <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>(704) 315-5820</span>
              <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>requests@cllocating.com</span>
            </div>
          </div>
          <div class="doc-id">
            <div class="doc-label">Team Expenses</div>
            <div class="doc-number">#EXP-${currentWeek}-${currentYear}</div>
          </div>
        </div>
        
        <!-- Info Section -->
        <div class="info-section">
          <div class="info-grid">
            <div class="info-item">
              <label>Report Type</label>
              <span>Team Summary</span>
            </div>
            <div class="info-item">
              <label>Period</label>
              <span>${weekStart ? format(weekStart, 'MMMM d') : ''} - ${weekEnd ? format(weekEnd, 'MMMM d, yyyy') : ''}</span>
            </div>
            <div class="info-item">
              <label>Team Total</label>
              <span class="total-badge">${formatCurrency(totalAmount)}</span>
            </div>
          </div>
        </div>
        
        <!-- Team Table -->
        <table>
          <thead>
            <tr>
              <th>Employee</th>
              <th style="text-align: center;">Status</th>
              <th style="text-align: center;">Expenses</th>
              <th style="text-align: right;">Total Amount</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
            <tr class="total-row">
              <td colspan="2" style="text-align: right; font-weight: 600;">TEAM TOTAL:</td>
              <td style="text-align: center;">${totalExpenses}</td>
              <td style="text-align: right; color: #059669; font-weight: 700; font-size: 14px;">${formatCurrency(totalAmount)}</td>
            </tr>
          </tbody>
        </table>
        
        <!-- Footer -->
        <div class="footer">
          Generated from CenterLine Locating FieldReady on ${format(new Date(), 'MMMM d, yyyy h:mm a')}
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    toast.success('Team expense print view opened');
  };

  const getStatusBadge = (status, rejectionReason) => {
    // If Draft but has rejection reason, show as "Returned"
    if (status === 'Draft' && rejectionReason) {
      return <Badge className="bg-orange-100 text-orange-700 border border-orange-300">Returned</Badge>;
    }
    
    const styles = {
      'Draft': 'bg-slate-100 text-slate-700',
      'Submitted': 'bg-amber-100 text-amber-700',
      'Approved': 'bg-emerald-100 text-emerald-700',
      'Rejected': 'bg-red-100 text-red-700',
      'Paid': 'bg-green-100 text-green-700 border border-green-300'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Barlow_Condensed'] flex items-center gap-3">
            <Receipt className="w-8 h-8 text-amber-500" />
            Expense Reports
          </h1>
          <p className="text-slate-500 mt-1">Track and submit weekly expenses</p>
        </div>
      </div>

      {/* Week Navigator */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <Button variant="outline" onClick={() => navigateWeek(-1)} className="w-full sm:w-auto">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <div className="text-center">
              <p className="text-lg font-bold text-slate-900">
                Week {currentWeek}, {currentYear}
              </p>
              <p className="text-sm text-slate-500">
                {weekStart && weekEnd && (
                  `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`
                )}
              </p>
            </div>
            <Button variant="outline" onClick={() => navigateWeek(1)} className="w-full sm:w-auto">
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="mt-4 text-center">
            <Button 
              className="bg-amber-500 hover:bg-amber-400 text-slate-900"
              onClick={loadOrCreateReport}
            >
              <Plus className="w-4 h-4 mr-2" />
              {myReports.find(r => r.week_number === currentWeek && r.year === currentYear)
                ? 'Edit This Week\'s Report'
                : 'Start This Week\'s Report'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* My Assigned Jobs — quick-pick + search, always visible above the
          tabs (not tucked in a dropdown) so starting a job-linked expense
          is one tap instead of hunting through the Job select later. */}
      <Card className="border-slate-200" data-testid="expense-my-jobs-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-['Barlow_Condensed'] flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-amber-500" />
            {canExpenseAnyJob ? 'All Jobs' : 'My Assigned Jobs'}
          </CardTitle>
          <p className="text-xs text-slate-500">Tap a job to start this week's expense report for it</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {jobs.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No jobs currently assigned to you</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="expense-my-jobs-grid">
              {(() => {
                const shown = expenseJobSearch
                  ? filterJobsByQuery(jobs, expenseJobSearch)
                  : jobs.slice(0, 6);
                if (shown.length === 0) {
                  return (
                    <p className="text-sm text-slate-400 italic col-span-full">
                      No jobs match &ldquo;{expenseJobSearch}&rdquo;
                    </p>
                  );
                }
                return shown.map(job => (
                  <button
                    key={job.id}
                    type="button"
                    data-testid={`expense-job-card-${job.id}`}
                    onClick={() => openWeekReportForJob(job)}
                    className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-white hover:border-amber-400 hover:bg-amber-50/50 transition-colors text-left"
                  >
                    {job.job_number && (
                      <span className="font-mono text-xs font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded flex-shrink-0">
                        {job.job_number}
                      </span>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{job.client_name || job.job_title}</p>
                      {job.job_title && job.client_name && (
                        <p className="text-xs text-slate-500 truncate">{job.job_title}</p>
                      )}
                    </div>
                  </button>
                ));
              })()}
            </div>
          )}
          {!expenseJobSearch && jobs.length > 6 && (
            <p className="text-xs text-slate-400">+{jobs.length - 6} more — search below to find them</p>
          )}
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="expense-job-search-input"
              placeholder="Search by customer, address, job # or keyword…"
              value={expenseJobSearch}
              onChange={(e) => setExpenseJobSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="bg-slate-100 w-max md:w-auto">
            <TabsTrigger value="my-reports" className="data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">
              <FileText className="w-4 h-4 mr-1 sm:mr-2" />
              My Reports ({myReports.length})
            </TabsTrigger>
            {isManager && (
              <>
                <TabsTrigger value="team-reports" className="data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">
                  <Users className="w-4 h-4 mr-1 sm:mr-2" />
                  Team
                </TabsTrigger>
                <TabsTrigger value="pending" className="data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">
                  <Clock className="w-4 h-4 mr-1 sm:mr-2" />
                  Pending ({pendingReports.length})
                </TabsTrigger>
                <TabsTrigger value="all" className="data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">
                  All ({allReports.length})
                </TabsTrigger>
              </>
            )}
          </TabsList>
        </div>

        {/* My Reports Tab */}
        <TabsContent value="my-reports" className="space-y-3 mt-4">
          {myReports.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-12 text-center">
                <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No expense reports yet</p>
                <p className="text-sm text-slate-400 mt-2">Click "Start This Week's Report" above to create one</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {myReports.map(report => (
                <Card 
                  key={report.id}
                  className={`border-slate-200 hover:border-amber-300 transition-colors cursor-pointer ${report.rejection_reason && report.status === 'Draft' ? 'border-l-4 border-l-amber-500' : ''}`}
                  onClick={() => { setPreviewReport(report); setShowPreview(true); }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-bold text-slate-900">Week {report.week_number}, {report.year}</span>
                          {getStatusBadge(report.status, report.rejection_reason)}
                        </div>
                        <p className="text-sm text-slate-500">
                          {report.week_start} to {report.week_end} • {report.expenses?.length || 0} expenses
                        </p>
                        {report.rejection_reason && report.status === 'Draft' && (
                          <p className="text-xs text-amber-600 mt-1">Needs correction: {report.rejection_reason}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-emerald-600">{formatCurrency(report.total_amount)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Team Reports Tab (Manager only) */}
        {isManager && (
          <TabsContent value="team-reports" className="space-y-6 mt-4">
            {/* Team Summary Card with Export */}
            <Card className="border-slate-200 bg-amber-50/30">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="text-center md:text-left">
                    <h3 className="font-semibold text-slate-900">Week {currentWeek}, {currentYear} Summary</h3>
                    <p className="text-sm text-slate-500">
                      {weekStart && weekEnd && `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`}
                    </p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-sm">
                        <span className="font-bold text-emerald-600">
                          {formatCurrency(allReports.filter(r => r.week_number === currentWeek && r.year === currentYear).reduce((sum, r) => sum + (r.total_amount || 0), 0))}
                        </span>
                        <span className="text-slate-500 ml-1">team total</span>
                      </span>
                      <span className="text-sm">
                        <span className="font-bold text-slate-700">
                          {allReports.filter(r => r.week_number === currentWeek && r.year === currentYear).length}
                        </span>
                        <span className="text-slate-500 ml-1">reports</span>
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleExportTeamExpenseCSV} data-testid="export-team-expense-csv-btn">
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                    <Button className="bg-amber-500 hover:bg-amber-600 text-white" size="sm" onClick={handlePrintTeamExpenseView} data-testid="export-team-expense-pdf-btn">
                      <Download className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teamMembers.map(member => {
                const memberReportCount = allReports.filter(r => r.user_id === member.id).length;
                const currentWeekReport = allReports.find(
                  r => r.user_id === member.id && r.week_number === currentWeek && r.year === currentYear
                );
                
                return (
                  <Card 
                    key={member.id}
                    className={`border-slate-200 hover:border-amber-500/50 transition-colors ${
                      selectedTeamMember?.id === member.id ? 'ring-2 ring-amber-500' : ''
                    }`}
                  >
                    <CardContent className="p-4">
                      <div 
                        className="flex items-start gap-3 cursor-pointer"
                        onClick={() => handleSelectTeamMember(member)}
                      >
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center font-bold flex-shrink-0 shadow-sm"
                          style={{ backgroundColor: member.calendar_color || '#f59e0b', color: getContrastTextColor(member.calendar_color || '#f59e0b') }}
                        >
                          {member.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 truncate">{member.full_name}</h3>
                          <p className="text-xs text-slate-500 truncate">{member.email}</p>
                          <Badge variant="outline" className="text-xs mt-1 capitalize">{member.role}</Badge>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-slate-600">{memberReportCount}</p>
                          <p className="text-xs text-slate-500">reports</p>
                        </div>
                      </div>
                      
                      {/* Current Week Status */}
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        {currentWeekReport ? (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">Week {currentWeek}:</span>
                            <div className="flex items-center gap-2">
                              {getStatusBadge(currentWeekReport.status, currentWeekReport.rejection_reason)}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditTeamMemberReport(currentWeekReport);
                                }}
                                title="Edit report"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-500">No report for Week {currentWeek}</span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCreateReportForMember(member);
                              }}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Create
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Selected Team Member's Reports */}
            {selectedTeamMember && (
              <Card className="border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shadow-sm"
                      style={{ backgroundColor: selectedTeamMember.calendar_color || '#f59e0b', color: getContrastTextColor(selectedTeamMember.calendar_color || '#f59e0b') }}
                    >
                      {selectedTeamMember.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    {selectedTeamMember.full_name}'s Expense Reports
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedTeamMember(null)}>
                    Close
                  </Button>
                </CardHeader>
                <CardContent>
                  {teamMemberReports.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No expense reports found</p>
                      <Button 
                        className="mt-3 bg-amber-500 hover:bg-amber-400 text-slate-900"
                        onClick={() => handleCreateReportForMember(selectedTeamMember)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Report for Week {currentWeek}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {teamMemberReports.map(report => (
                        <div 
                          key={report.id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Week {report.week_number}, {report.year}</span>
                              {getStatusBadge(report.status, report.rejection_reason)}
                            </div>
                            <p className="text-sm text-slate-500">
                              {report.expenses?.length || 0} expenses • {formatCurrency(report.total_amount)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setPreviewReport(report); setShowPreview(true); }}
                              title="View report"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditTeamMemberReport(report)}
                              title="Edit report"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}

        {/* Pending Approval Tab (Manager only) */}
        {isManager && (
          <TabsContent value="pending" className="space-y-3 mt-4">
            {pendingReports.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <Check className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No pending expense reports</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {pendingReports.map(report => (
                  <Card 
                    key={report.id}
                    className="border-slate-200 hover:border-amber-300 transition-colors cursor-pointer"
                    onClick={() => { setPreviewReport(report); setShowPreview(true); }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <span className="font-bold text-slate-900">{report.user_name}</span>
                            <Badge variant="outline">Week {report.week_number}</Badge>
                          </div>
                          <p className="text-sm text-slate-500">
                            Submitted {report.submitted_at ? format(new Date(report.submitted_at), 'MMM d, yyyy') : ''} • {report.expenses?.length || 0} expenses
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <p className="text-lg font-bold text-emerald-600">{formatCurrency(report.total_amount)}</p>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              className="bg-emerald-500 hover:bg-emerald-400"
                              onClick={(e) => { e.stopPropagation(); handleApprove(report.id); }}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setReportToReject(report.id); 
                                setShowRejectDialog(true); 
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {/* All Reports Tab (Manager only) */}
        {isManager && (
          <TabsContent value="all" className="space-y-3 mt-4">
            <div className="grid gap-3">
              {allReports.map(report => (
                <Card 
                  key={report.id}
                  className="border-slate-200 hover:border-amber-300 transition-colors cursor-pointer"
                  onClick={() => { setPreviewReport(report); setShowPreview(true); }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-bold text-slate-900">{report.user_name}</span>
                          <Badge variant="outline">Week {report.week_number}, {report.year}</Badge>
                          {getStatusBadge(report.status, report.rejection_reason)}
                        </div>
                        <p className="text-sm text-slate-500">
                          {report.expenses?.length || 0} expenses
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="text-lg font-bold text-emerald-600">{formatCurrency(report.total_amount)}</p>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleExportCSV(report.id); }}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Expense Report Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={(open) => {
        setShowEditor(open);
        if (!open) {
          setEditingForUser(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap text-base md:text-lg">
              <Receipt className="w-5 h-5 md:w-6 md:h-6 text-amber-500 shrink-0" />
              <span className="truncate">Week {currentReport?.week_number}, {currentReport?.year}</span>
              {editingForUser && (
                <Badge className="bg-amber-100 text-amber-700 text-xs">
                  for {editingForUser.name}
                </Badge>
              )}
            </DialogTitle>
            <p className="text-sm text-slate-500">
              {currentReport?.week_start} to {currentReport?.week_end}
            </p>
          </DialogHeader>

          {currentReport && (
            <div className="space-y-6 py-4">
              {/* User Info - show team member if editing for someone else */}
              <div className="p-3 md:p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-3 md:gap-4">
                  <div 
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: editingForUser ? '#f59e0b' : '#fef3c7' }}
                  >
                    <span className={editingForUser ? 'text-white font-bold text-base md:text-lg' : 'text-amber-700 font-bold text-base md:text-lg'}>
                      {editingForUser 
                        ? editingForUser.name?.split(' ').map(n => n[0]).join('').slice(0, 2)
                        : user?.full_name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">
                      {editingForUser ? editingForUser.name : user?.full_name}
                    </p>
                    <p className="text-sm text-slate-500 truncate">
                      {editingForUser ? editingForUser.email : user?.email}
                    </p>
                    {editingForUser && (
                      <Badge variant="outline" className="mt-1 text-xs bg-amber-50 text-amber-700 border-amber-300">
                        Editing as Admin
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Rejection Reason Banner - shown when report was returned for corrections */}
              {currentReport.rejection_reason && currentReport.status === 'Draft' && (
                <div className="p-3 sm:p-4 bg-amber-50 rounded-lg border border-amber-300" data-testid="rejection-reason-banner">
                  <div className="flex items-start gap-3">
                    <X className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-amber-800">Report Returned for Corrections</p>
                      <p className="text-sm text-amber-700 mt-1">{currentReport.rejection_reason}</p>
                      {currentReport.rejected_by && (
                        <p className="text-xs text-amber-600 mt-2">
                          Returned by {currentReport.rejected_by}
                          {currentReport.rejected_at && ` on ${format(new Date(currentReport.rejected_at), 'MMM d, yyyy')}`}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Locked banner — explains why the form below isn't editable,
                  instead of a regular user silently seeing nothing (e.g.
                  when arriving here via the Job page's "Add Expense" link
                  for a week that's already Submitted/Approved/Paid). */}
              {currentReport.status !== 'Draft' && !isManager && (
                <div className="p-3 sm:p-4 bg-slate-100 rounded-lg border border-slate-200" data-testid="expense-report-locked-banner">
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-slate-700">This report is locked for edits</p>
                      <p className="text-sm text-slate-500 mt-1">
                        Week {currentReport.week_number}'s report is already {currentReport.status.toLowerCase()}, so new expenses can't be added here.
                        {currentReport.status === 'Submitted' && ' Ask a manager to return it for corrections if changes are needed.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Add Expense Form - Show for Draft status OR if admin is editing */}
              {(currentReport.status === 'Draft' || isManager) && (
                <Card className="border-amber-200 bg-amber-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-['Barlow_Condensed']">Add Expense</CardTitle>
                    <p className="text-xs text-slate-500 mt-1">
                      Auto-saves when you click Add - upload receipts anytime.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Row 1: Date, Job */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Date *</Label>
                        <Input
                          type="date"
                          value={newExpense.date}
                          onChange={(e) => setNewExpense(prev => ({ ...prev, date: e.target.value }))}
                          min={currentReport.week_start}
                          max={currentReport.week_end}
                          className="bg-white w-full"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Job</Label>
                        <Select
                          value={newExpense.job_id || "none"}
                          onValueChange={(val) => {
                            if (val === "other") {
                              setNewExpense(prev => ({ 
                                ...prev, 
                                job_id: 'other',
                                job_number: 'OTHER',
                                custom_job_description: prev.custom_job_description || ''
                              }));
                            } else {
                              const actualVal = val === "none" ? "" : val;
                              const job = (editingForUser ? allJobs : jobs).find(j => j.id === actualVal);
                              setNewExpense(prev => ({ 
                                ...prev, 
                                job_id: actualVal,
                                job_number: job?.job_number || '',
                                custom_job_description: ''
                              }));
                            }
                          }}
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue placeholder="Select job" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No job</SelectItem>
                            {(editingForUser ? allJobs : jobs).map(job => (
                              <SelectItem key={job.id} value={job.id}>
                                {job.job_number} - {job.client_name}
                              </SelectItem>
                            ))}
                            <SelectItem value="other" className="border-t border-slate-200 mt-1 pt-1">
                              <span className="text-slate-600 italic">Other (Non-job expense)</span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {/* Show custom description field when "Other" is selected */}
                        {newExpense.job_id === 'other' && (
                          <div className="mt-2">
                            <Input
                              value={newExpense.custom_job_description || ''}
                              onChange={(e) => setNewExpense(prev => ({ ...prev, custom_job_description: e.target.value }))}
                              placeholder="e.g., Office supplies, Training..."
                              className="bg-white text-sm"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Row 2: Category, Amount */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Category *</Label>
                        <Select
                          value={newExpense.category}
                          onValueChange={(val) => setNewExpense(prev => ({ ...prev, category: val }))}
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {EXPENSE_CATEGORIES.map(cat => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Amount *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newExpense.amount ?? ''}
                          onChange={(e) => setNewExpense(prev => ({ ...prev, amount: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                          placeholder="0.00"
                          className="bg-white"
                        />
                      </div>
                    </div>
                    {/* Row 3: Description + Add Button */}
                    <div className="flex flex-col md:flex-row gap-3">
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs">Description *</Label>
                        <Input
                          value={newExpense.description}
                          onChange={(e) => setNewExpense(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Brief description"
                          className="bg-white"
                        />
                      </div>
                      <Button onClick={handleAddExpense} className="md:mt-5 bg-amber-500 hover:bg-amber-400 text-slate-900 w-full md:w-auto">
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Expenses List */}
              <div>
                <h3 className="font-semibold mb-3">Expenses ({currentReport.expenses?.length || 0})</h3>
                {(!currentReport.expenses || currentReport.expenses.length === 0) ? (
                  <p className="text-slate-500 text-center py-8">No expenses added yet</p>
                ) : (
                  <>
                    {/* Mobile card layout */}
                    <div className="block md:hidden space-y-2">
                      {currentReport.expenses.map((expense) => (
                        <div key={expense.id} className="p-3 bg-white rounded-lg border border-slate-200">
                          <div className="flex justify-between items-start mb-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium">{expense.date}</p>
                              <p className="text-xs text-slate-500 truncate">
                                {expense.job_number === 'OTHER' ? `Other: ${expense.custom_job_description || ''}` : expense.job_number || '-'}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="font-bold text-emerald-600">{formatCurrency(expense.amount)}</span>
                              {(currentReport.status === 'Draft' || isManager) && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                                  onClick={() => handleRemoveExpense(expense.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <Badge variant="outline" className="text-xs shrink-0">{expense.category}</Badge>
                            {expense.receipt_url ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 h-6 px-2 text-xs"
                                onClick={() => fetchReceiptImage(currentReport.id, expense.id, expense.receipt_filename)}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                Receipt
                              </Button>
                            ) : (currentReport.status === 'Draft' || isManager) ? (
                              <div className="relative">
                                <input
                                  type="file"
                                  accept="image/*,application/pdf,.pdf"
                                  className="absolute inset-0 opacity-0 cursor-pointer"
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                      handleReceiptUpload(expense.id, e.target.files[0]);
                                    }
                                  }}
                                />
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  disabled={uploadingReceipt === expense.id} 
                                  className={`h-6 px-2 text-xs ${expense.receipt_filename ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : ''}`}
                                >
                                  {uploadingReceipt === expense.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : expense.receipt_filename ? (
                                    <>
                                      <Camera className="w-3 h-3 mr-1" />
                                      Re-upload
                                    </>
                                  ) : (
                                    <>
                                      <Camera className="w-3 h-3 mr-1" />
                                      Upload
                                    </>
                                  )}
                                </Button>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">No receipt</span>
                            )}
                          </div>
                          {expense.description && (
                            <p className="text-xs text-slate-500 mt-2 truncate">{expense.description}</p>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Desktop table layout */}
                    <div className="hidden md:block overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="whitespace-nowrap">Date</TableHead>
                            <TableHead className="whitespace-nowrap">Job</TableHead>
                            <TableHead className="whitespace-nowrap">Category</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="text-right whitespace-nowrap">Amount</TableHead>
                            <TableHead className="whitespace-nowrap">Receipt</TableHead>
                            {(currentReport.status === 'Draft' || isManager) && <TableHead></TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {currentReport.expenses.map((expense) => (
                            <TableRow key={expense.id}>
                              <TableCell className="whitespace-nowrap">{expense.date}</TableCell>
                              <TableCell>
                                {expense.job_number === 'OTHER' ? (
                                  <span className="text-slate-600 italic text-xs" title={expense.custom_job_description}>
                                    Other
                                  </span>
                                ) : (
                                  <span className="text-xs">{expense.job_number || '-'}</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-xs whitespace-nowrap">{expense.category}</Badge>
                              </TableCell>
                              <TableCell>{expense.description}</TableCell>
                              <TableCell className="text-right font-medium whitespace-nowrap">{formatCurrency(expense.amount)}</TableCell>
                              <TableCell>
                                {expense.receipt_url ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 h-7 px-2"
                                    onClick={() => fetchReceiptImage(
                                      currentReport.id,
                                      expense.id,
                                      expense.receipt_filename
                                    )}
                                    data-testid={`view-receipt-${expense.id}`}
                                  >
                                    <Eye className="w-3 h-3 mr-1" />
                                    View
                                  </Button>
                                ) : (currentReport.status === 'Draft' || isManager) ? (
                                  <div className="relative">
                                    <input
                                      type="file"
                                      accept="image/*,application/pdf,.pdf"
                                      className="absolute inset-0 opacity-0 cursor-pointer"
                                      onChange={(e) => {
                                        if (e.target.files?.[0]) {
                                          handleReceiptUpload(expense.id, e.target.files[0]);
                                        }
                                      }}
                                    />
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      disabled={uploadingReceipt === expense.id} 
                                      className={`h-7 px-2 ${expense.receipt_filename ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : ''}`}
                                    >
                                      {uploadingReceipt === expense.id ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : expense.receipt_filename ? (
                                        <>
                                          <Camera className="w-3 h-3 mr-1" />
                                          Re-upload
                                        </>
                                      ) : (
                                        <>
                                          <Camera className="w-3 h-3 mr-1" />
                                          Upload
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </TableCell>
                              {(currentReport.status === 'Draft' || isManager) && (
                                <TableCell>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                                    onClick={() => handleRemoveExpense(expense.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                )}
              </div>

              {/* Total */}
              <div className="flex justify-end border-t pt-4">
                <div className="text-right">
                  <p className="text-sm text-slate-500">Total Expenses</p>
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(currentReport.total_amount)}</p>
                </div>
              </div>

              {/* Notes */}
              {(currentReport.status === 'Draft' || isManager) && (
                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Input
                    value={currentReport.notes || ''}
                    onChange={(e) => setCurrentReport(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any additional notes for this report..."
                    className="bg-slate-50"
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex flex-col md:flex-row justify-between gap-3">
            {currentReport?.status === 'Draft' && (
              <Button 
                variant="destructive" 
                onClick={handleDeleteDraft} 
                disabled={saving}
                className="md:mr-auto w-full md:w-auto order-last md:order-first"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Draft
              </Button>
            )}
            {(currentReport?.status === 'Draft' || isManager) && (
              <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                <Button variant="outline" onClick={() => setShowEditor(false)} className="w-full md:w-auto">
                  Cancel
                </Button>
                <Button variant="outline" onClick={handleSaveReport} disabled={saving} className="w-full md:w-auto">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save {currentReport?.status !== 'Draft' ? 'Changes' : 'Draft'}
                </Button>
                {currentReport?.status === 'Draft' && (
                  <Button 
                    className="bg-amber-500 hover:bg-amber-400 text-slate-900 w-full md:w-auto"
                    onClick={handleSubmitReport}
                    disabled={saving || !currentReport?.expenses?.length}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Submit for Approval
                  </Button>
                )}
              </div>
            )}
            {currentReport?.status !== 'Draft' && !isManager && (
              <Button variant="outline" onClick={() => setShowEditor(false)} className="w-full md:w-auto">
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {previewReport && (
            <>
              <DialogHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                  <div>
                    <DialogTitle>Week {previewReport.week_number}, {previewReport.year}</DialogTitle>
                    <p className="text-sm text-slate-500">{previewReport.week_start} to {previewReport.week_end}</p>
                  </div>
                  {getStatusBadge(previewReport.status, previewReport.rejection_reason)}
                </div>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* User Info */}
                <div className="p-3 md:p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                      <span className="text-amber-700 font-bold text-base md:text-lg">
                        {previewReport.user_name?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{previewReport.user_name}</p>
                      <p className="text-sm text-slate-500 truncate">{previewReport.user_email || 'No email on record'}</p>
                    </div>
                  </div>
                </div>

                {/* Mobile-friendly expenses list - show on screens smaller than md (768px) */}
                <div className="block md:hidden space-y-2">
                  {previewReport.expenses?.map((expense, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-sm font-medium">{expense.date}</p>
                          <p className="text-xs text-slate-500">
                            {expense.job_number === 'OTHER' ? 'Other' : expense.job_number || '-'}
                          </p>
                        </div>
                        <span className="font-bold text-emerald-600">{formatCurrency(expense.amount)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-xs">{expense.category}</Badge>
                        {expense.receipt_url ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 h-6 px-2 text-xs"
                            onClick={() => fetchReceiptImage(previewReport.id, expense.id, expense.receipt_filename)}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Receipt
                          </Button>
                        ) : expense.receipt_filename ? (
                          <span className="text-xs text-amber-600 italic">Re-upload via Edit</span>
                        ) : (
                          <span className="text-xs text-slate-400">No receipt</span>
                        )}
                      </div>
                      {expense.description && (
                        <p className="text-xs text-slate-500 mt-2 truncate">{expense.description}</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Desktop table - show on md and larger */}
                <div className="hidden md:block overflow-x-auto">
                  <Table className="min-w-[500px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Date</TableHead>
                        <TableHead className="whitespace-nowrap">Job</TableHead>
                        <TableHead className="whitespace-nowrap">Category</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right whitespace-nowrap">Amount</TableHead>
                        <TableHead className="whitespace-nowrap">Receipt</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewReport.expenses?.map((expense, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="whitespace-nowrap">{expense.date}</TableCell>
                          <TableCell>
                            {expense.job_number === 'OTHER' ? (
                              <span className="text-slate-600 italic text-xs">Other</span>
                            ) : (
                              <span className="text-xs">{expense.job_number || '-'}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs whitespace-nowrap">{expense.category}</Badge>
                          </TableCell>
                          <TableCell>{expense.description}</TableCell>
                          <TableCell className="text-right font-medium whitespace-nowrap">{formatCurrency(expense.amount)}</TableCell>
                          <TableCell>
                            {expense.receipt_url ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 h-7 px-2"
                                onClick={() => fetchReceiptImage(
                                  previewReport.id,
                                  expense.id,
                                  expense.receipt_filename
                                )}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                View
                              </Button>
                            ) : expense.receipt_filename ? (
                              <span className="text-amber-600 text-xs italic">Re-upload via Edit</span>
                            ) : (
                              <span className="text-slate-400 text-xs">No</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex justify-end border-t pt-4">
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Total</p>
                    <p className="text-2xl font-bold text-emerald-600">{formatCurrency(previewReport.total_amount)}</p>
                  </div>
                </div>

                {previewReport.notes && (
                  <div className="p-3 sm:p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm font-semibold text-slate-600 mb-1">Notes</p>
                    <p className="text-sm text-slate-500">{previewReport.notes}</p>
                  </div>
                )}

                {previewReport.status === 'Rejected' && previewReport.rejection_reason && (
                  <div className="p-3 sm:p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm font-semibold text-red-600 mb-1">Rejection Reason</p>
                    <p className="text-sm text-red-500">{previewReport.rejection_reason}</p>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-wrap gap-2">
                <Button variant="outline" onClick={() => handleExportCSV(previewReport.id)} data-testid="export-csv-btn">
                  <Download className="w-4 h-4 mr-2" />
                  CSV
                </Button>
                <Button 
                  onClick={() => { setShowPreview(false); navigate(`/expense-report/${previewReport.id}/preview`); }} 
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="print-save-pdf-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print / Save PDF
                </Button>
                <Button variant="outline" onClick={() => handleEmailReport(previewReport)} data-testid="email-report-btn">
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
                {/* Edit button for admins or draft reports owned by user */}
                {(isManager || (previewReport.status === 'Draft' && previewReport.user_id === user?.id)) && (
                  <Button 
                    variant="outline"
                    className="bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={() => {
                      // Close preview and open editor
                      setShowPreview(false);
                      if (previewReport.user_id !== user?.id) {
                        setEditingForUser({
                          id: previewReport.user_id,
                          name: previewReport.user_name,
                          email: previewReport.user_email
                        });
                      }
                      setCurrentReport(previewReport);
                      setShowEditor(true);
                    }}
                    data-testid="edit-report-btn"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )}
                {isManager && previewReport.status === 'Submitted' && (
                  <>
                    <Button 
                      variant="destructive"
                      onClick={() => { setReportToReject(previewReport.id); setShowRejectDialog(true); }}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                    <Button 
                      className="bg-emerald-500 hover:bg-emerald-400"
                      onClick={() => handleApprove(previewReport.id)}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                  </>
                )}
                {/* Delete Report button for super_user */}
                {isSuperUser && (
                  <Button 
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    onClick={async () => {
                      if (window.confirm(`Delete this entire expense report (${previewReport.report_number || 'Week ' + previewReport.week_number})? This cannot be undone.`)) {
                        try {
                          await axios.delete(
                            `${API}/expense-reports/${previewReport.id}`,
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          setShowPreview(false);
                          setPreviewReport(null);
                          toast.success('Expense report deleted');
                          // Refresh data after closing dialog
                          setTimeout(() => {
                            fetchData();
                          }, 100);
                        } catch (error) {
                          console.error('Delete error:', error);
                          toast.error(error.response?.data?.detail || 'Failed to delete report');
                        }
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Report
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject Expense Report</AlertDialogTitle>
            <AlertDialogDescription>
              Please provide a reason for rejecting this expense report.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Input
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection..."
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setRejectReason(''); setReportToReject(null); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleReject} className="bg-red-500 hover:bg-red-600">
              Reject Report
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Receipt Viewer Dialog */}
      <Dialog open={!!viewingReceipt} onOpenChange={(open) => !open && closeReceiptViewer()}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-amber-500" />
              Receipt: {viewingReceipt?.filename}
            </DialogTitle>
          </DialogHeader>
          {viewingReceipt && (
            <div className="flex flex-col items-center">
              <div className="w-full overflow-auto max-h-[65vh] bg-slate-100 rounded-lg flex items-center justify-center p-4">
                {loadingReceipt ? (
                  <div className="flex flex-col items-center gap-2 py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                    <p className="text-slate-500">Loading receipt...</p>
                  </div>
                ) : receiptImageUrl ? (
                  <img
                    src={receiptImageUrl}
                    alt={viewingReceipt.filename}
                    className="max-w-full h-auto rounded shadow-lg"
                    data-testid="receipt-image"
                  />
                ) : (
                  <p className="text-slate-500">Failed to load receipt</p>
                )}
              </div>
              <div className="flex gap-2 mt-4">
                {receiptImageUrl && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = receiptImageUrl;
                      link.download = viewingReceipt.filename || 'receipt';
                      link.click();
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                )}
                <Button onClick={closeReceiptViewer}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
